import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Grid,
    Typography,
    useTheme,
    Card,
    CardContent,
    CardHeader,
    LinearProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Skeleton,
    alpha,
} from '@mui/material';
import {
    AccessTime as AccessTimeIcon,
    Assignment as AssignmentIcon,
    BeachAccess as BeachAccessIcon,
    CheckCircle as CheckCircleIcon,
    RadioButtonUnchecked as UncheckedIcon,
    CalendarToday as CalendarIcon,
    Flag as FlagIcon,
} from '@mui/icons-material';
import { differenceInCalendarDays, endOfYear, format, max as maxDate, min as minDate, parseISO, startOfMonth, startOfYear } from 'date-fns';
import { getPDIs, getTasks, getTimeEntries, getUserProjects, getVacationRequests } from '../services/collaborator';
import { PDI, Task, TimeEntry, VacationRequest, Project } from '../types/collaborator';

const isGoalDone = (goal: any) => goal.completed === true || goal.status === 'COMPLETED';

const formatDay = (value?: string | null) => {
    if (!value) return '';
    try {
        return format(parseISO(value), 'dd/MM/yyyy');
    } catch {
        return value;
    }
};

/** Dias de férias aprovados que caem no ano corrente. */
const approvedVacationDaysThisYear = (requests: VacationRequest[]) => {
    const yearStart = startOfYear(new Date());
    const yearEnd = endOfYear(new Date());
    return requests
        .filter((r) => r.status === 'APPROVED')
        .reduce((total, r) => {
            const start = maxDate([parseISO(r.start_date), yearStart]);
            const end = minDate([parseISO(r.end_date), yearEnd]);
            return end >= start ? total + differenceInCalendarDays(end, start) + 1 : total;
        }, 0);
};

const TaskItem: React.FC<{ task: Task }> = ({ task }) => {
    const theme = useTheme();
    return (
        <ListItem sx={{ px: 2, py: 1.5, '&:hover': { backgroundColor: theme.palette.action.hover, borderRadius: 1 } }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
                {task.completed ? <CheckCircleIcon fontSize="small" color="success" /> : <UncheckedIcon fontSize="small" color="action" />}
            </ListItemIcon>
            <ListItemText
                primary={task.title}
                primaryTypographyProps={{
                    variant: 'body2',
                    sx: {
                        textDecoration: task.completed ? 'line-through' : 'none',
                        color: task.completed ? 'text.secondary' : 'text.primary',
                    },
                }}
                secondaryTypographyProps={{ component: 'div' }}
                secondary={task.due_date ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        <CalendarIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.disabled' }} />
                        <Typography variant="caption" color="text.disabled">{formatDay(task.due_date)}</Typography>
                    </Box>
                ) : undefined}
            />
        </ListItem>
    );
};

const PDIProgress: React.FC<{ pdi: PDI | null; loading: boolean }> = ({ pdi, loading }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const goals = pdi?.goals || [];
    const done = goals.filter(isGoalDone).length;
    const progress = goals.length ? Math.round((done / goals.length) * 100) : 0;
    const deadline = (pdi as any)?.end_date;

    return (
        <Card elevation={0}>
            <CardHeader
                title={t('dashboard.pdi.title')}
                titleTypographyProps={{ variant: 'h6' }}
                action={deadline ? (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <FlagIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.disabled' }} />
                        <Typography variant="caption" color="text.disabled">
                            {t('dashboard.pdi.dueDate')}: {formatDay(deadline)}
                        </Typography>
                    </Box>
                ) : undefined}
            />
            <CardContent>
                {loading ? (
                    <Skeleton variant="rounded" height={80} />
                ) : !pdi ? (
                    <Typography variant="body2" color="text.secondary">Nenhum PDI cadastrado.</Typography>
                ) : (
                    <>
                        <Typography variant="subtitle2" gutterBottom>{pdi.title}</Typography>
                        <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">{t('dashboard.pdi.progress')}</Typography>
                                <Typography variant="body2" color="primary" fontWeight={600}>{progress}%</Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={progress}
                                sx={{ height: 6, borderRadius: 3, backgroundColor: alpha(theme.palette.primary.main, 0.1) }}
                            />
                        </Box>
                        {goals.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">PDI sem metas cadastradas.</Typography>
                        ) : (
                            <List disablePadding>
                                {goals.map((goal) => (
                                    <ListItem key={goal.id} sx={{ px: 0, py: 0.75 }}>
                                        <ListItemIcon sx={{ minWidth: 28 }}>
                                            {isGoalDone(goal) ? <CheckCircleIcon fontSize="small" color="success" /> : <UncheckedIcon fontSize="small" color="action" />}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={goal.title}
                                            primaryTypographyProps={{
                                                variant: 'body2',
                                                sx: {
                                                    textDecoration: isGoalDone(goal) ? 'line-through' : 'none',
                                                    color: isGoalDone(goal) ? 'text.secondary' : 'text.primary',
                                                },
                                            }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
};

const Dashboard: React.FC = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [loading, setLoading] = useState(true);
    const [entries, setEntries] = useState<TimeEntry[]>([]);
    const [projects, setProjects] = useState<Project[]>([]);
    const [vacations, setVacations] = useState<VacationRequest[]>([]);
    const [pdis, setPdis] = useState<PDI[]>([]);
    const [tasks, setTasks] = useState<Task[]>([]);

    useEffect(() => {
        const today = new Date();
        const monthStart = format(startOfMonth(today), 'yyyy-MM-dd');
        const todayStr = format(today, 'yyyy-MM-dd');
        // Cada bloco é independente: uma falha não esconde os demais
        Promise.allSettled([
            getTimeEntries(monthStart, todayStr).then(setEntries),
            getUserProjects().then(setProjects),
            getVacationRequests().then(setVacations),
            getPDIs().then(setPdis),
            getTasks().then(setTasks),
        ]).finally(() => setLoading(false));
    }, []);

    const hours = entries.reduce((sum, e) => sum + Number(e.hours || 0), 0);
    const latestPdi = pdis.length ? [...pdis].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))[0] : null;
    const pendingTasks = tasks.filter((task) => !task.completed)
        .sort((a, b) => (a.due_date || '9999').localeCompare(b.due_date || '9999'))
        .slice(0, 5);

    const stats = [
        {
            icon: AccessTimeIcon,
            title: `${t('dashboard.stats.hoursLogged')} (mês)`,
            value: `${Number.isInteger(hours) ? hours : hours.toFixed(1)}h`,
            color: theme.palette.primary.main,
        },
        {
            icon: AssignmentIcon,
            title: t('dashboard.stats.activeProjects'),
            value: String(projects.filter((p) => !p.status || p.status === 'ACTIVE').length),
            color: theme.palette.success.main,
        },
        {
            icon: BeachAccessIcon,
            title: 'Dias de férias aprovados no ano',
            value: String(approvedVacationDaysThisYear(vacations)),
            color: theme.palette.info.main,
        },
    ];

    return (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Grid container spacing={3}>
                {stats.map((stat, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                        <Card elevation={0} sx={{ height: '100%', backgroundColor: alpha(stat.color, 0.05), border: 'none' }}>
                            <CardContent sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <stat.icon sx={{ color: stat.color, mr: 1, fontSize: 20 }} />
                                    <Typography variant="body2" color="text.secondary">{stat.title}</Typography>
                                </Box>
                                {loading ? (
                                    <Skeleton width={60} height={42} />
                                ) : (
                                    <Typography variant="h4" color="text.primary" sx={{ fontWeight: 600 }}>{stat.value}</Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                ))}

                <Grid item xs={12} md={6}>
                    <PDIProgress pdi={latestPdi} loading={loading} />
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card elevation={0}>
                        <CardHeader title="Tarefas pendentes" titleTypographyProps={{ variant: 'h6' }} />
                        <CardContent>
                            {loading ? (
                                <Skeleton variant="rounded" height={80} />
                            ) : pendingTasks.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">Nenhuma tarefa pendente.</Typography>
                            ) : (
                                <List disablePadding>
                                    {pendingTasks.map((task, index) => (
                                        <React.Fragment key={task.id}>
                                            <TaskItem task={task} />
                                            {index < pendingTasks.length - 1 && <Divider component="li" variant="middle" />}
                                        </React.Fragment>
                                    ))}
                                </List>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Dashboard;
